import { FC, useState } from 'react';
import type { AdminDemoRequestListItem, AdminDemoRequestPeriod } from '@libs/types';
import { Avatar, Box, Button, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ListPagination } from '@components';
import { useTrans } from '@libs/i18n';

import { useAdminDemoRequests } from '../api/admin-api';
import { formatDate } from '../components/adminFormat';
import { AdminTable } from '../components/AdminTable';
import { adminButtonSx, adminColors } from '../components/adminTheme';
import { DemoRequestDetailDialog } from '../components/DemoRequestDetailDialog';

const PAGE_SIZE = 20;

const requesterInitials = (r: AdminDemoRequestListItem) =>
  (r.fullName || r.workEmail).slice(0, 2).toUpperCase();

export const AdminDemoRequestsPage: FC = observer(() => {
  const theme = useTheme();
  const c = adminColors(theme);
  const btn = adminButtonSx(theme);
  const { t } = useTrans();
  const [period, setPeriod] = useState<AdminDemoRequestPeriod>('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminDemoRequestListItem | null>(null);

  const { data, isLoading, isFetching, isError } = useAdminDemoRequests({ period, page, limit: PAGE_SIZE });
  const rows = data?.rows ?? [];
  const meta = data?.meta;

  const periods: { key: AdminDemoRequestPeriod; label: string }[] = [
    { key: 'all', label: t('app.admin.demo.filter.all') },
    { key: 'day', label: t('app.admin.demo.filter.day') },
    { key: 'week', label: t('app.admin.demo.filter.week') },
    { key: 'month', label: t('app.admin.demo.filter.month') },
  ];

  const columns = [
    { key: 'requester', label: t('app.admin.demo.table.requester'), width: '22%' },
    { key: 'email', label: t('app.admin.demo.table.email'), width: '24%' },
    { key: 'company', label: t('app.admin.demo.table.company'), width: '20%' },
    { key: 'jobTitle', label: t('app.admin.demo.table.jobTitle'), width: '16%' },
    { key: 'requested', label: t('app.admin.demo.table.requested'), width: '18%' },
  ];

  const selectPeriod = (next: AdminDemoRequestPeriod) => {
    setPeriod(next);
    setPage(1);
  };

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: 3.5, py: 2.75, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2.25 }}>
        <Typography sx={{ fontSize: 23, fontWeight: 700, color: c.ink, mb: 0.375 }}>
          {t('app.admin.demo.title')}
        </Typography>
        <Typography sx={{ fontSize: 13, color: c.sub }}>{t('app.admin.demo.subtitle')}</Typography>
      </Box>

      {/* Period filter — newest requesters within the last day / week / month */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        {periods.map((p) => {
          const active = period === p.key;
          return (
            <Button
              key={p.key}
              onClick={() => selectPeriod(p.key)}
              sx={{
                ...(active ? btn.solid : btn.neutral),
                fontSize: 12.5,
                px: 1.75,
                py: 0.625,
                borderRadius: 2,
              }}
            >
              {p.label}
            </Button>
          );
        })}
      </Box>

      <AdminTable
        columns={columns}
        minWidth={900}
        isLoading={isLoading}
        isError={isError}
        errorLabel={t('app.admin.demo.error')}
        isEmpty={!rows.length}
        emptyLabel={t('app.admin.demo.empty')}
        footer={
          meta && meta.totalCount > 0 ? (
            <ListPagination
              page={meta.currentPage}
              totalPages={meta.totalPages}
              onPageChange={setPage}
              isLoading={isFetching}
              sx={{ mt: 0 }}
            />
          ) : undefined
        }
      >
        {rows.map((r) => (
          <TableRow key={r.id} hover onClick={() => setSelected(r)} sx={{ cursor: 'pointer' }}>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.125 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 600, background: 'linear-gradient(135deg,#623AE6,#0063B3)' }}>
                  {requesterInitials(r)}
                </Avatar>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.ink }}>{r.fullName}</Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>{r.workEmail}</TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.ink }}>{r.companyName}</TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>{r.jobTitle || '—'}</TableCell>
            <TableCell sx={{ borderBottom: `1px solid ${c.border}`, fontSize: 12.5, color: c.sub }}>{formatDate(r.createdAt)}</TableCell>
          </TableRow>
        ))}
      </AdminTable>

      <DemoRequestDetailDialog request={selected} onClose={() => setSelected(null)} />
    </Box>
  );
});
